import { createTheme } from '@mui/material/styles'

# Create MUI theme matching existing react-md color scheme
# Colors are from common.scss CSS variables
theme = createTheme
  palette:
    mode: 'light'
    primary:
      main: '#9C27B0'      # --primary-500
      light: '#BA68C8'     # --primary-300
      dark: '#7B1FA2'      # --primary-700
      50: '#F3E5F5'
      100: '#E1BEE7'
      200: '#CE93D8'
      300: '#BA68C8'
      400: '#AB47BC'
      500: '#9C27B0'
      600: '#8E24AA'
      700: '#7B1FA2'
      800: '#6A1B9A'
      900: '#4A148C'
      contrastText: '#fff'
    secondary:
      main: '#2196F3'      # --secondary-500
      light: '#64B5F6'     # --secondary-300
      dark: '#1976D2'      # --secondary-700
      50: '#E3F2FD'
      100: '#BBDEFB'
      200: '#90CAF9'
      300: '#64B5F6'
      400: '#42A5F5'
      500: '#2196F3'
      600: '#1E88E5'
      700: '#1976D2'
      800: '#1565C0'
      900: '#0D47A1'
      contrastText: '#fff'
    background:
      default: '#c0c0c0'   # Light gray background
      paper: '#ffffff'
    text:
      primary: 'rgba(0, 0, 0, 0.87)'
      secondary: 'rgba(0, 0, 0, 0.6)'

  typography:
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
    fontSize: 14
    body1:
      fontSize: 14
      lineHeight: 1.4
    h4:
      fontSize: '1.26em'
      lineHeight: 1.4
      fontWeight: 700
      textTransform: 'uppercase'

  shape:
    borderRadius: 20  # $cornerRadius from common.scss

  mixins:
    toolbar:
      minHeight: 56
      '@media (min-width:0px)':
        '@media (orientation: landscape)':
          minHeight: 48
      '@media (min-width:600px)':
        minHeight: 64
    appBar:
      minHeight: 56
      '@media (min-width:0px)':
        '@media (orientation: landscape)':
          minHeight: 48
      '@media (min-width:600px)':
        minHeight: 64

  breakpoints:
    values:
      xs: 0
      sm: 600
      md: 840    # Match react-md REACT_MD_DESKTOP_MIN
      lg: 1280
      xl: 1920

  components:
    MuiCard:
      defaultProps:
        elevation: 2
      styleOverrides:
        root:
          borderRadius: 20  # $cornerRadius
          overflow: 'hidden'

    MuiButton:
      defaultProps:
        disableElevation: false
      styleOverrides:
        root:
          borderRadius: 10  # $buttonCornerRadius (cornerRadius / 2)
          textTransform: 'none'  # react-md didn't uppercase buttons
          # THE HOUSE TAP-TARGET STANDARD IS 40px. Owner ruling, card
          # 6aa4d8bd1c031a8daf2e49f7. MUI's contained default is 36.5px, which
          # the phone audit measured identically on eight different screens —
          # Continue, Complete Application, Next, Get Started. Apple and Android
          # guidance is 44 and 48; 40 is the number this product adopted.
          #
          # On `root` rather than per size, so a button does not fall under the
          # standard by being spelled size="small". minHeight (not height) so a
          # button with wrapped or multi-line content still grows.
          minHeight: 40

    MuiDialog:
      styleOverrides:
        container:
          display: 'flex'
          alignItems: 'center'
          justifyContent: 'center'
          width: '100vw'
          height: '100vh'
        paper:
          borderRadius: 20  # $cornerRadius
          margin: 'auto'
          # Safe-area-inset handling for mobile devices
          maxHeight: 'calc(100vh - 2 * max(env(safe-area-inset-top), env(safe-area-inset-bottom), 24px))'

    MuiAppBar:
      styleOverrides:
        root:
          backgroundColor: 'transparent'
          color: 'rgba(155, 39, 176, 0.87)'  # Match .rmd-app-bar__title from common.scss

    MuiFab:
      styleOverrides:
        root:
          position: 'fixed'

    # Form components to match react-md behavior
    MuiTextField:
      defaultProps:
        variant: 'outlined'
        fullWidth: true

    MuiSelect:
      defaultProps:
        variant: 'outlined'

    MuiMenu:
      styleOverrides:
        paper:
          borderRadius: 20  # $cornerRadius
          border: '1px solid rgba(0, 0, 0, 0.2)'
          boxShadow: '0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px -1px rgb(0 0 0 / 40%)'

console.log 'THEME OBJECT:', theme
console.log 'THEME MIXINS:', theme.mixins
console.log 'THEME MIXINS KEYS:', Object.keys(theme.mixins || {})

# THE SAME THEME, DARK (card 6ac075e0270c80c6c2166c4a, owner 2026-10-02): what
# a screen gets while the device is in dark mode (color_scheme.coffee decides).
# The app's purple and blue at the lighter tones Material uses on dark — #9C27B0
# on #1e1e1e does not read — and the surfaces from color_scheme.scss's dark set.
# Everything else (type, shape, breakpoints, component defaults) is the light
# theme's, so a screen changes color and nothing else.
p = theme.palette
export darkTheme = createTheme
  palette:
    mode: 'dark'
    primary: Object.assign {}, p.primary, {main: p.primary[200], light: p.primary[100], dark: p.primary[400], contrastText: 'rgba(0, 0, 0, 0.87)'}
    secondary: Object.assign {}, p.secondary, {main: p.secondary[200], light: p.secondary[100], dark: p.secondary[400], contrastText: 'rgba(0, 0, 0, 0.87)'}
    background:
      default: '#121212'
      paper: '#1e1e1e'
  typography: theme.typography
  shape: theme.shape
  mixins: theme.mixins
  breakpoints: {values: theme.breakpoints.values}
  components: Object.assign {}, theme.components,
    MuiAppBar:
      styleOverrides:
        root:
          backgroundColor: 'transparent'
          backgroundImage: 'none'
          color: p.primary[200]
    MuiMenu:
      styleOverrides:
        paper:
          borderRadius: 20
          border: '1px solid rgba(255, 255, 255, 0.12)'

export default theme
